import { useEffect } from "react";

import { reportDeviceChange, type ReportedSurface } from "./telemetry";

const INPUT_KINDS: ReadonlySet<MediaDeviceKind> = new Set([
  "audioinput",
  "videoinput",
]);

export interface DeviceListDiff {
  added: string[];
  removed: string[];
}

const describeDevice = (device: MediaDeviceInfo): string =>
  `${device.kind}:${device.label || "unlabelled"}`;

const isInput = (device: MediaDeviceInfo): boolean =>
  INPUT_KINDS.has(device.kind);

/**
 * What changed between two device lists. Devices are matched on `deviceId`,
 * since labels repeat across identical hardware.
 */
export function diffDeviceLists(
  before: MediaDeviceInfo[],
  after: MediaDeviceInfo[]
): DeviceListDiff {
  const beforeIds = new Set(before.map((device) => device.deviceId));
  const afterIds = new Set(after.map((device) => device.deviceId));

  return {
    added: after
      .filter((device) => !beforeIds.has(device.deviceId))
      .map(describeDevice),
    removed: before
      .filter((device) => !afterIds.has(device.deviceId))
      .map(describeDevice),
  };
}

const countKind = (devices: MediaDeviceInfo[], kind: MediaDeviceKind): number =>
  devices.filter((device) => device.kind === kind).length;

/**
 * Breadcrumbs each device swap while a capture surface is open, with the added
 * and removed devices.
 *
 * @param enabled Pass the surface's permission state. Before permission,
 * `enumerateDevices` returns placeholder entries with blank ids, so the diff
 * would report every device as added the first time permission lands.
 */
export function useDeviceChangeBreadcrumbs(
  enabled: boolean,
  captureSurface: ReportedSurface
): void {
  useEffect(() => {
    const mediaDevices = navigator.mediaDevices;
    if (
      !enabled ||
      mediaDevices?.enumerateDevices == null ||
      mediaDevices.addEventListener == null
    ) {
      return;
    }

    let previous: MediaDeviceInfo[] = [];
    let cancelled = false;

    const snapshot = () =>
      mediaDevices
        .enumerateDevices()
        .then((devices) => devices.filter(isInput))
        // Telemetry must never break capture, and a device list we cannot read
        // is exactly the state this reports on.
        .catch(() => null);

    // Queue up a snapshot so that we don't report the initial device list as a
    // change.
    let queue = snapshot().then((devices) => {
      if (!cancelled && devices != null) previous = devices;
    });

    const onDeviceChange = () => {
      queue = queue
        .then(snapshot)
        .then((devices) => {
          if (cancelled || devices == null) return;
          const diff = diffDeviceLists(previous, devices);
          previous = devices;
          reportDeviceChange(captureSurface, diff, {
            audioInputs: countKind(devices, "audioinput"),
            videoInputs: countKind(devices, "videoinput"),
          });
        })
        .catch(() => undefined);
    };

    mediaDevices.addEventListener("devicechange", onDeviceChange);
    return () => {
      cancelled = true;
      mediaDevices.removeEventListener("devicechange", onDeviceChange);
    };
  }, [enabled, captureSurface]);
}
